import { useMemo } from "react";
import { NavItem } from "../components/NavItem/NavItem";
import type { NavIdType } from "../types";

const handleClick = ({
  setActive,
  id,
  itemOnClick
}: {
  setActive: React.Dispatch<NavIdType | undefined>;
  id: NavIdType;
  itemOnClick?: (args: any) => void;
}) => {
  setActive(id);
  typeof itemOnClick === "function" && itemOnClick({id});
};

export const itemConstructor = ({
  id,
  type,
  items,
  size,
  textLimitType,
  textLimit,
  animation,
  active,
  setActive,
  onActiveItem,
  mode,
  variant,
  boldText,
  bgColor,
  skeleton,
  itemsObjByIndexMap, 
  itemsObjByIdMap
}) => {

  /**
   * Construct Items
   */
  const _items = useMemo(() => {
    const hasItems = items && items.length;
    let _items = items;
 
    /**
     * items is array of strings
     */
    if (hasItems && typeof items[0] === "string") {
      _items = items.map((item, index) => {
        const { _label, lineClampClasses } = truncateLabel({
          label: item,
          textLimitType,
          textLimit,
        });
        const _id = createId(id, item, index);;
        const _onItemClick = () => handleClick({ setActive, id: _id });

        return (
          <NavItem
            variant={variant}
            label={item}
            id={_id}
            type={type}
            className={lineClampClasses}
            size={size}
            key={_id}
            active={active}
            animation={animation}
            mode={mode}
            aria-label={`Button ${item ? `for ${item}` : ""}`}
            onClick={_onItemClick}
            boldText={boldText}
            // bgColor={bgColor}
            itemsObjByIdMap={itemsObjByIdMap}
            itemsObjByIndexMap={itemsObjByIndexMap}
            skeleton={skeleton}
            role="menuitem"
          />
        );
      });

      /**
       * items is object array, checking for 'old' pill nav props, and new nav item props
       */
    } else if (hasItems && typeof items[0] === "object") {
      _items = items.map((item, index) => {
        // Parse label
        const { _label, lineClampClasses } = truncateLabel({
          label: item?.label,
          textLimitType,
          textLimit,
        });

        const _id = createId(id, item, index);;
        const _onItemClick = () => handleClick({ setActive, id: _id, itemOnClick: item?.onClick });

        let _item = item;

        // Parse old pill props
        if ("iconName" in item || "team" in item || "sport" in item) {
            _item = {...item, leadingIcon: item?.iconName };
        }
   
        return (
            <NavItem
              variant={variant}
              {..._item}
              type={type}
              label={_label}
              size={size}
              active={active}
              mode={mode}
              className={lineClampClasses}
              itemsObjByIdMap={itemsObjByIdMap}
              itemsObjByIndexMap={itemsObjByIndexMap}
              boldText={boldText}
              bgColor={_item?.bgColor}
              skeleton={skeleton}
              onClick={_onItemClick}
              key={`cui-nav-${id}-item-${index}`}
            />
          );

      });
    }
    return _items;
  }, [
    id,
    items,
    size,
    textLimitType,
    textLimit,
    animation,
    active,
    setActive,
    mode,
    onActiveItem,
    variant,
    skeleton,
    boldText,
    bgColor,
    type
  ]);

  return { _items, itemsObjByIndexMap }
};

/**
 * Helpers
 */

const lineClampMap = {
  1: "cui-line-clamp-1",
  2: "cui-line-clamp-2",
  3: "cui-line-clamp-3",
};

function truncateLabel({ label, textLimitType, textLimit }) {
  if (!label) return { _label: "", lineClampClasses: lineClampMap["1"]  };
  let _label = label;
  let lineClampClasses = "";
  const hasTextLimit = textLimit && typeof textLimit === "number";
  if (textLimitType === "character" && hasTextLimit)
    _label = `${label}`.slice(0, textLimit);
  if (textLimitType === "line" && hasTextLimit)
    lineClampClasses =
      textLimit in lineClampMap ? lineClampMap[textLimit] : lineClampMap["1"];
  return { _label, lineClampClasses };
}

export function createId(navId, item, index) {
    let _id = `${navId}-item-${index}`;
    if (typeof item === "object" && "id" in item && item?.id !== undefined) _id = item.id
    return _id;
}